"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.AdjustmentFileHandler = void 0;
var _apiStateClient = require("@abacus/api-state-client");
var _utils = require("../utils/utils");
var _adjustmentFileValidator = require("./adjustmentFileValidator");
var _config = require("@/config");
var _excel = require("@/utils/excel");
var _types = require("../types");
var _adjustmentDataCache = require("@/chunk/adjustmentDataCache");
class AdjustmentFileHandler {
  constructor({
    localStorageClient,
    logger,
    royaltiesClient,
    s3Client,
    snowflakeClient,
    stateClient,
    statementPeriodAdjustmentFileId
  }) {
    this.localStorageClient = localStorageClient;
    this.logger = logger;
    this.royaltiesClient = royaltiesClient;
    this.s3Client = s3Client;
    this.snowflakeClient = snowflakeClient;
    this.stateClient = stateClient;
    this.statementPeriodAdjustmentFileId = statementPeriodAdjustmentFileId;
  }

  /**
   * Get adjustment file path and download it from s3.
   */
  async getAdjustmentFileInfo() {
    // Get the file's info
    const fileInfo = await this.royaltiesClient.getStatementPeriodAdjustmentFile(this.statementPeriodAdjustmentFileId);

    // Check the file's location
    const s3FilePath = fileInfo.valid_file_location;
    if (!s3FilePath) {
      throw new Error("Invalid statement period adjustment file ID");
    }
    if (!s3FilePath.endsWith(".xlsx")) {
      throw new Error("Not a valid excel file");
    }

    // Download the file
    const filePath = await this.downloadAdjustmentFile(s3FilePath);

    // Return the file name and local file path
    return [fileInfo, filePath];
  }

  /**
   * Get 'close_balance' abacus_state for payment_entities in statement_period
   */
  async getCloseBalanceStatuses(statementPeriodId) {
    const paymentEntityStates = await this.royaltiesClient.getStatementPeriodPaymentEntityStates(statementPeriodId);
    const closeBalanceStatusMap = new Map();
    for (const state of paymentEntityStates) {
      if (state.action_name === _types.AbacusStateActionName.CloseBalance) {
        closeBalanceStatusMap.set(state.reference_payment_entity_id, state.action_status);
      }
    }
    return closeBalanceStatusMap;
  }

  /**
   * Get adjustment file path and download it from s3
   */
  async downloadAdjustmentFile(s3FilePath) {
    const url = new URL(s3FilePath);
    const bucket = url.hostname;
    const fileName = url.pathname.replace(/^\//, ""); // Remove leading slash
    const stream = await this.s3Client.readFile(fileName, {
      bucket
    });

    // Download to a temporary file path
    const filePath = await (0, _utils.getTempFilePath)(".xlsx");
    await this.localStorageClient.writeFile(filePath, stream);

    // Return the local file path
    return filePath;
  }

  /**
   * Validate adjustment file
   */
  async process() {
    let tempFilePath = undefined;
    try {
      // Get the file's info
      let fileInfo;
      [fileInfo, tempFilePath] = await this.getAdjustmentFileInfo();
      await this.validateAdjustmentFile(fileInfo.file_name, tempFilePath, fileInfo.statement_period_id, fileInfo.statement_period_adjustment_file_id);
    } catch (error) {
      await this.recordErrorState();
      throw error;
    } finally {
      if (tempFilePath != null) {
        await this.localStorageClient.deleteFile(tempFilePath);
      }
    }
  }
  async recordErrorState() {
    const statementPeriodAdjustmentFileStates = await this.stateClient.createEntityAbacusStates({
      parent_table_id: this.statementPeriodAdjustmentFileId,
      parent_table_name: "statement-period-adjustment-file"
    });
    const state = statementPeriodAdjustmentFileStates.find(state => {
      return state.action_name === _types.AbacusStateActionName.UploadFile;
    });
    if (state != null) {
      console.log(JSON.stringify({
        path: "updateState",
        abacusStateId: state.abacus_state_id,
        actionStatus: _apiStateClient.AbacusStateActionStatus.Error
      }));
      /*
      await this.stateClient.updateState({
        abacusStateId: state.abacus_state_id,
        actionStatus: AbacusStateActionStatus.Error,
      });
      */
    }
  }

  /**
   * Validate adjustment file
   * @param actualFileName name of the uploaded adjustment_file
   * @param tempFilePath file path to the downloaded S3 file in the temp dir
   * @param paymentEntityCloseBalanceStatuses map of 'close_balance' statuses
   */
  async validateAdjustmentFile(actualFileName, tempFilePath, statementPeriodId, adjustmentFileId) {
    console.log("Adjustment file validation is not implemented yet. This is a placeholder.");
    const cache = new _adjustmentDataCache.AdjustmentDataCache({
      capacity: _config.config.cacheSize
    });
    cache.paymentEntityCloseBalanceStatuses = await this.getCloseBalanceStatuses(statementPeriodId);
    const adjustmentTypeStream = await this.snowflakeClient.getAdjustmentTypes();
    for await (const {
      name
    } of adjustmentTypeStream) {
      cache.adjustmentTypes.add(name.toLowerCase());
    }
    const adjustmentFile = new _adjustmentFileValidator.AdjustmentFileValidator({
      cache,
      excelClient: new _excel.XLSXClient(),
      fileId: adjustmentFileId,
      fileName: actualFileName,
      filePath: tempFilePath,
      logger: this.logger,
      s3Client: this.s3Client,
      snowflakeClient: this.snowflakeClient
    });
    const {
      totalValidRows,
      totalInvalidRows,
      totalFileAmount,
      roundedAmount,
      error,
      errorType
    } = await adjustmentFile.validate();
    const putBody = {
      valid_row_count: totalValidRows,
      invalid_row_count: totalInvalidRows,
      total_file_amount_multicurrency: "" + totalFileAmount,
      total_rounded_amount_multicurrency: "" + roundedAmount
    };
    if (error != null) {
      putBody.invalid_file_location = (0, _utils.getS3ErrorFilePath)(_config.config.s3.bucketName, adjustmentFileId);
      putBody.error_type = "content_error";
    }
    if (errorType == "Empty File") {
      putBody.error_type = "content_error";
    }
    console.log(JSON.stringify({
      path: "updateStatementPeriodAdjustmentFile",
      adjustmentFileId,
      statementPeriodId,
      putBody
    }));
    /*
    await this.royaltiesClient.updateStatementPeriodAdjustmentFile(
      statementPeriodId,
      adjustmentFileId,
      putBody,
    );
    */

    if (error != null) {
      console.info(`Failed to upload adjustments: ${error}`);
    }
  }
}
exports.AdjustmentFileHandler = AdjustmentFileHandler;